import React, { useContext, useMemo } from "react";
import { Badge, Box, Tab, Tabs } from "@mui/material";
import { useNavigate, useLocation, matchRoutes } from "react-router-dom";
import {
  QUEUE_URL,
  ORDERS_URL,
  VENDOR_ORDERS_URL,
  COMPLETED_POS_URL,
  SAM_GOV_OPPORTUNITIES_URL,
} from "../MainAppPanel";
import { AppContext } from "../AppContextProvider";
import { useWorkQueuePurchaseOrders } from "../hooks/useWorkQueuePurchaseOrders";
import {
  countOrderConfNeededQueueItems,
  countOrdersQueueItems,
  countPendingInboundPoQueueItems,
  countWorkQueueRfqItems,
} from "../utils/purchaseOrderWorkQueue";

const badgeBaseSx = {
  position: "static",
  transform: "none",
  minWidth: 18,
  height: 18,
  borderRadius: "50%",
  fontSize: "0.65rem",
  fontWeight: 700,
  // Unselected MUI tabs fade the whole label — keep counts fully visible.
  opacity: "1 !important",
} as const;

const redBadgeSx = {
  "& .MuiBadge-badge": {
    ...badgeBaseSx,
    bgcolor: "#d32f2f !important",
    color: "#fff !important",
  },
} as const;

const tealBadgeSx = {
  "& .MuiBadge-badge": {
    ...badgeBaseSx,
    bgcolor: "#b4ece3 !important",
    color: "#0f766e !important",
  },
} as const;

const WorkQueueTabs: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestForQuotes = [] } = useContext(AppContext);
  const { purchaseOrders } = useWorkQueuePurchaseOrders();

  const workQueueRfqCount = useMemo(
    () => countWorkQueueRfqItems(requestForQuotes, purchaseOrders),
    [requestForQuotes, purchaseOrders]
  );

  const pendingPoCount = useMemo(
    () => countPendingInboundPoQueueItems(requestForQuotes, purchaseOrders),
    [requestForQuotes, purchaseOrders]
  );

  const ordersCount = useMemo(
    () => countOrdersQueueItems(requestForQuotes, purchaseOrders),
    [requestForQuotes, purchaseOrders]
  );

  const orderConfNeededCount = useMemo(
    () => countOrderConfNeededQueueItems(requestForQuotes, purchaseOrders),
    [requestForQuotes, purchaseOrders]
  );

  const getSelectedTab = () => {
    if (matchRoutes([{ path: QUEUE_URL }], location.pathname)) return 0;
    if (matchRoutes([{ path: ORDERS_URL }], location.pathname)) return 1;
    if (matchRoutes([{ path: VENDOR_ORDERS_URL }], location.pathname)) return 2;
    if (matchRoutes([{ path: COMPLETED_POS_URL }], location.pathname)) return 3;
    if (matchRoutes([{ path: SAM_GOV_OPPORTUNITIES_URL }], location.pathname))
      return 4;
    return 0;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate(QUEUE_URL);
        break;
      case 1:
        navigate(ORDERS_URL);
        break;
      case 2:
        navigate(VENDOR_ORDERS_URL);
        break;
      case 3:
        navigate(COMPLETED_POS_URL);
        break;
      case 4:
        navigate(SAM_GOV_OPPORTUNITIES_URL);
        break;
    }
  };

  return (
    <Tabs
      value={getSelectedTab()}
      onChange={handleTabChange}
      aria-label="work queue tabs"
      sx={{
        minHeight: 40,
        flex: "0 1 auto",
        // Default unselected tabs use opacity 0.6, which fades badges.
        // Keep full opacity and mute unselected labels via color instead.
        "& .MuiTab-root": {
          opacity: 1,
          minHeight: 40,
          minWidth: 0,
          px: 1,
          py: 0.5,
          fontSize: "0.8125rem",
        },
        "& .MuiTab-root:not(.Mui-selected)": {
          color: "text.secondary",
        },
      }}
    >
      <Tab
        key="queue"
        label={
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            {workQueueRfqCount > 0 ? (
              <Badge
                badgeContent={workQueueRfqCount}
                max={99999}
                sx={tealBadgeSx}
              />
            ) : null}
            Work Queue
            {pendingPoCount > 0 ? (
              <Badge
                badgeContent={pendingPoCount}
                color="error"
                max={99999}
                sx={redBadgeSx}
              />
            ) : null}
          </Box>
        }
      />
      <Tab
        key="orders"
        label={
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            Orders
            {ordersCount > 0 ? (
              <Badge
                badgeContent={ordersCount}
                color="error"
                max={99999}
                sx={redBadgeSx}
              />
            ) : null}
          </Box>
        }
      />
      <Tab
        key="order-conf-needed"
        label={
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            Order Conf Needed
            {orderConfNeededCount > 0 ? (
              <Badge
                badgeContent={orderConfNeededCount}
                color="error"
                max={99999}
                sx={redBadgeSx}
              />
            ) : null}
          </Box>
        }
      />
      <Tab key="completed-pos" label="Completed PO's" />
      <Tab key="sam-gov" label="SAM.gov" />
    </Tabs>
  );
};

export default WorkQueueTabs;
